<template>
  <div class="tinto-page">
    <Loading :isActive="loading" />

    <!-- ===== CONSULTA =====
         A integração abre os outros campos: catálogo e filial só existem dentro
         dela, então perguntar antes disso não tem resposta. -->
    <div class="tinto-consulta">
      <h3 class="tinto-consulta__titulo">Por que esta base não aparece para esta cor?</h3>
      <p class="tinto-consulta__sub">
        A consulta percorre a mesma régua da venda — cor ativa, filial atendida, catálogo da máquina,
        vínculo de EAN, fórmula e liberação do produto — e mostra em qual passo parou.
      </p>

      <div class="tinto-consulta__grade">
        <div class="tinto-consulta__campo tinto-consulta__campo--largo">
          <label class="tinto-consulta__label">Integração</label>
          <select class="tinto-consulta__controle" v-model="consulta.integracaoId" @change="aoTrocarIntegracao()">
            <option value="">Selecione</option>
            <option v-for="integracao in integracoes" :key="integracao.id" :value="String(integracao.id)">
              {{ integracao.descricao }}
            </option>
          </select>
        </div>

        <div class="tinto-consulta__campo tinto-consulta__campo--medio">
          <label class="tinto-consulta__label">Filial</label>
          <select class="tinto-consulta__controle" v-model="consulta.filialId" :disabled="!integracao">
            <option value="">Selecione</option>
            <option v-for="filial in filiaisDaIntegracao" :key="filial.id" :value="filial.id">
              {{ filial.nome }}
            </option>
          </select>
          <span class="tinto-consulta__ajuda tinto-consulta__ajuda--alerta" v-if="integracao && !filiaisDaIntegracao.length">
            Esta integração não atende nenhuma filial. Vincule na aba Integrações.
          </span>
        </div>

        <!-- A régua de disponibilidade do produto muda por canal: o mesmo item
             pode aparecer no Call Center e não aparecer no B2B. -->
        <div class="tinto-consulta__campo">
          <label class="tinto-consulta__label">Canal</label>
          <select class="tinto-consulta__controle" v-model="consulta.canal" :disabled="!integracao">
            <option v-for="canal in CANAIS" :key="canal.valor" :value="canal.valor">{{ canal.nome }}</option>
          </select>
          <span class="tinto-consulta__ajuda">Por onde a venda aconteceria — muda a régua do último passo.</span>
        </div>
      </div>

      <div class="tinto-consulta__grade" style="margin-top: 16px">
        <div class="tinto-consulta__campo tinto-consulta__campo--largo">
          <label class="tinto-consulta__label">Cor</label>
          <multiselect
            v-model="cor.selecionado"
            :options="cor.opcoes"
            :loading="cor.carregando"
            :disabled="!integracao"
            :internal-search="false"
            :clear-on-select="false"
            :preserve-search="true"
            :show-labels="false"
            placeholder="Buscar por nome ou código"
            track-by="chave"
            label="nome"
            @search-change="buscarComEspera('cor', $event)"
            @open="abrirBusca('cor')"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <span class="tinto-opcao">
                <span class="tinto-opcao__swatch" :style="{ background: option.hexa || '#f2f4f7' }"></span>
                <span class="tinto-opcao__texto">{{ option.nome }}</span>
                <span class="tinto-opcao__meta">{{ option.cod }}</span>
              </span>
            </template>
            <template slot="option" slot-scope="{ option }">
              <span class="tinto-opcao">
                <span class="tinto-opcao__swatch" :style="{ background: option.hexa || '#f2f4f7' }"></span>
                <span class="tinto-opcao__texto">
                  {{ option.nome }}
                  <small class="tinto-opcao__sub">{{ option.cod }} · {{ option.origem }}</small>
                </span>
                <span v-if="option.inativa" class="tinto-opcao__marca">inativa</span>
              </span>
            </template>
            <template slot="noOptions"><span class="tinto-opcao__vazio">Digite para buscar…</span></template>
            <template slot="noResult"><span class="tinto-opcao__vazio">Nenhuma cor deste fabricante com esse termo.</span></template>
          </multiselect>
        </div>

        <div class="tinto-consulta__campo tinto-consulta__campo--largo">
          <label class="tinto-consulta__label">Base</label>
          <multiselect
            v-model="base.selecionado"
            :options="base.opcoes"
            :loading="base.carregando"
            :disabled="!integracao"
            :internal-search="false"
            :clear-on-select="false"
            :preserve-search="true"
            :show-labels="false"
            placeholder="Buscar por nome, código ou EAN"
            track-by="chave"
            label="nome"
            @search-change="buscarComEspera('base', $event)"
            @open="abrirBusca('base')"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <span class="tinto-opcao">
                <span class="tinto-opcao__texto">{{ option.nome }}</span>
                <span class="tinto-opcao__meta">{{ option.ean || 'sem EAN' }}</span>
              </span>
            </template>
            <template slot="option" slot-scope="{ option }">
              <span class="tinto-opcao">
                <span class="tinto-opcao__texto">
                  {{ option.nome }}
                  <small class="tinto-opcao__sub">{{ option.embalagem }} · {{ option.origem }}</small>
                </span>
                <span v-if="option.ean" class="tinto-opcao__meta">{{ option.ean }}</span>
                <span v-else class="tinto-opcao__marca">sem EAN</span>
              </span>
            </template>
            <template slot="noOptions"><span class="tinto-opcao__vazio">Digite para buscar…</span></template>
            <template slot="noResult"><span class="tinto-opcao__vazio">Nenhuma base deste fabricante com esse termo.</span></template>
          </multiselect>
          <span class="tinto-consulta__ajuda tinto-consulta__ajuda--alerta" v-if="baseSemEan">
            Base sem EAN vinculado — já é o motivo de ela não aparecer. Vincule na aba Bases.
          </span>
        </div>

        <div class="tinto-consulta__campo">
          <label class="tinto-consulta__label">Cliente <small>(opcional)</small></label>
          <multiselect
            v-model="cliente.selecionado"
            :options="cliente.opcoes"
            :loading="cliente.carregando"
            :disabled="!integracao"
            :internal-search="false"
            :clear-on-select="false"
            :preserve-search="true"
            :show-labels="false"
            placeholder="Buscar por nome ou CNPJ"
            track-by="chave"
            label="nome"
            @search-change="buscarComEspera('cliente', $event)"
            @open="abrirBusca('cliente')"
          >
            <template slot="option" slot-scope="{ option }">
              <span class="tinto-opcao">
                <span class="tinto-opcao__texto">
                  {{ option.nome }}
                  <small class="tinto-opcao__sub">{{ option.documento }}</small>
                </span>
              </span>
            </template>
            <template slot="noOptions"><span class="tinto-opcao__vazio">Digite para buscar…</span></template>
            <template slot="noResult"><span class="tinto-opcao__vazio">Nenhum cliente com esse termo.</span></template>
          </multiselect>
          <span class="tinto-consulta__ajuda">Sem cliente, preço e disponibilidade no ERP não são avaliados.</span>
        </div>
      </div>

      <div class="tinto-consulta__rodape">
        <span class="tinto-consulta__dica" v-if="!integracao">Escolha a integração para liberar os demais campos.</span>
        <span class="tinto-consulta__dica" v-else-if="!podeConsultar">Faltam filial, cor e base para consultar.</span>
        <div class="tinto-consulta__acoes">
          <button class="button" @click="limpar()" v-if="consulta.integracaoId">Limpar</button>
          <button class="button button-primary" @click="diagnosticar()" :disabled="!podeConsultar">Diagnosticar</button>
        </div>
      </div>
    </div>

    <!-- ===== RESULTADO ===== -->
    <div class="tinto-resultado" v-if="resultado">
      <div :class="['tinto-resultado__cabecalho', resultado.disponivel ? 'is-ok' : 'is-falha']">
        <span class="tinto-resultado__icone">
          <fa-icon :icon="resultado.disponivel ? 'check' : 'times'" />
        </span>
        <span>
          <span class="tinto-resultado__frase">{{ resultado.conclusao }}</span>
          <span class="tinto-resultado__resumo" v-if="consultado">
            <span>{{ consultado.integracao }}</span>
            <span>{{ consultado.filial }}</span>
            <span>{{ consultado.cor }}</span>
            <span>{{ consultado.base }}</span>
            <span v-if="consultado.cliente">{{ consultado.cliente }}</span>
            <span>{{ consultado.canal }}</span>
          </span>
        </span>
      </div>

      <div class="tinto-regua">
        <div
          v-for="(passo, indice) in resultado.passos"
          :key="passo.chave"
          :class="['tinto-regua__item', classeSituacao(passo.situacao)]"
        >
          <span class="tinto-regua__marca">
            <fa-icon v-if="passo.situacao === 'ok'" icon="check" />
            <fa-icon v-else-if="passo.situacao === 'falhou'" icon="times" />
            <span v-else>{{ indice + 1 }}</span>
          </span>
          <span>
            <span class="tinto-regua__titulo">{{ passo.titulo }}</span>
            <span class="tinto-regua__detalhe" v-if="passo.detalhe">{{ passo.detalhe }}</span>
            <span class="tinto-regua__detalhe tinto-regua__detalhe--vazio" v-else>Não avaliado</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "../../components/Loading";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import { get } from '@/helpers/apiRequest';

/**
 * Os três campos de busca seguem o mesmo roteiro (endpoint + como virar opção),
 * mudando só o mapeamento. `chave` é o que o multiselect usa para identidade;
 * `valor` é o que o diagnóstico recebe — para a base é o EAN, não o id.
 *
 * Cor e base são buscadas pelo FABRICANTE da máquina escolhida, não pela
 * máquina em si: o catálogo antigo ainda está sem origem definida (integracao_id
 * nulo) e ficaria invisível se filtrássemos pela integração. A origem de cada
 * registro aparece no rótulo, e é justamente o que o diagnóstico avalia depois —
 * "a cor existe, mas na outra máquina" é uma das respostas possíveis.
 */
/**
 * Canais de venda que o diagnóstico sabe avaliar. Os valores são os mesmos
 * nomes de sistema do backend (Sistema::CALLCENTER e companhia), que é como a
 * API escolhe a implementação da busca de produto.
 */
const CANAIS = [
  { valor: 'CALLCENTER', nome: 'Call Center' },
  { valor: 'B2B', nome: 'B2B' },
  { valor: 'FORCA_VENDAS', nome: 'Força de Vendas' },
];

const RECURSOS = {
  cor: {
    url: '/admin/tintometrico/cor',
    porFabricante: true,
    mapear: (item) => ({
      chave: `cor-${item.id}`,
      valor: item.id,
      nome: item.nome,
      cod: item.codigoExibicao || item.cod,
      hexa: item.hexa,
      inativa: !item.ativo,
      integracaoId: item.integracaoId,
      origem: item.integracaoNome || 'sem origem',
    }),
  },
  base: {
    url: '/admin/tintometrico/base',
    porFabricante: true,
    mapear: (item) => ({
      chave: `base-${item.id}`,
      valor: item.ean,
      nome: item.nome,
      ean: item.ean,
      embalagem: item.embalagemVolumeMl ? `${item.embalagemNome} (${item.embalagemVolumeMl} ml)` : (item.embalagemNome || 'sem embalagem'),
      integracaoId: item.integracaoId,
      origem: item.integracaoNome || 'sem origem',
    }),
  },
  cliente: {
    url: '/admin/clientes',
    porFabricante: false,
    mapear: (item) => ({
      chave: `cliente-${item.id}`,
      valor: item.id,
      nome: item.razSoc || item.fant || `Cliente ${item.id}`,
      documento: item.cnpj || '',
    }),
  },
};

export default {
  name: "tintometrico-diagnostico",
  components: { Loading, Multiselect },
  props: ['filiais', 'integracoes'],

  data() {
    return {
      loading: false,
      resultado: null,
      consultado: null,
      CANAIS,
      consulta: { integracaoId: '', filialId: '', canal: 'CALLCENTER' },
      cor: { opcoes: [], carregando: false, selecionado: null, espera: null, termo: '' },
      base: { opcoes: [], carregando: false, selecionado: null, espera: null, termo: '' },
      cliente: { opcoes: [], carregando: false, selecionado: null, espera: null, termo: '' },
    };
  },

  computed: {
    integracao() {
      return (this.integracoes || []).find((i) => String(i.id) === this.consulta.integracaoId) || null;
    },

    // Só as lojas que esta integração atende: perguntar por uma filial que ela
    // não atende sempre devolve a mesma resposta.
    filiaisDaIntegracao() {
      const vinculadas = (this.integracao && this.integracao.filiais) || [];

      return vinculadas
        .map((id) => ({ id: String(id), nome: (this.filiais || {})[id] || `Filial ${id}` }))
        .sort((a, b) => a.nome.localeCompare(b.nome));
    },

    baseSemEan() {
      return !!(this.base.selecionado && !this.base.selecionado.ean);
    },

    podeConsultar() {
      return !!(this.consulta.integracaoId
        && this.consulta.filialId
        && this.cor.selecionado
        && this.base.selecionado
        && this.base.selecionado.ean);
    },
  },

  methods: {
    // Trocar de máquina invalida tudo que veio da anterior — inclusive a filial,
    // que pertence ao vínculo da integração.
    aoTrocarIntegracao() {
      this.consulta.filialId = '';
      this.resultado = null;
      this.consultado = null;

      ['cor', 'base', 'cliente'].forEach((recurso) => {
        this[recurso].selecionado = null;
        this[recurso].opcoes = [];
      });

      if (this.filiaisDaIntegracao.length === 1) {
        this.consulta.filialId = this.filiaisDaIntegracao[0].id;
      }
    },

    abrirBusca(recurso) {
      if (!this[recurso].opcoes.length) {
        this.buscar(recurso, '');
      }
    },

    buscarComEspera(recurso, termo) {
      clearTimeout(this[recurso].espera);
      this[recurso].espera = setTimeout(() => this.buscar(recurso, termo || ''), 300);
    },

    buscar(recurso, termo) {
      const config = RECURSOS[recurso];

      if (config.porFabricante && !this.integracao) return;

      const campo = this[recurso];
      // `page` é obrigatório nas listagens de catálogo: sem ele a API devolve a
      // coleção inteira (milhares de cores), não a página.
      const params = new URLSearchParams({ page: '1' });

      if (termo) params.append('q', termo);
      if (config.porFabricante) params.append('fabricante_id', this.integracao.fabricanteId);

      campo.termo = termo;
      campo.carregando = true;

      get(`${config.url}?${params}`)
        .then((json) => {
          if (campo.termo !== termo) return; // resposta de uma digitação antiga

          const lista = json.itens || json.data || json;

          campo.opcoes = (Array.isArray(lista) ? lista : [])
            .map(config.mapear)
            .sort((a, b) => this.pesoDaOrigem(a) - this.pesoDaOrigem(b));
        })
        .catch(() => { campo.opcoes = []; })
        .finally(() => { campo.carregando = false; });
    },

    // Registro da máquina escolhida primeiro, depois o catálogo sem origem
    // (legado), por último o das outras máquinas do mesmo fabricante.
    pesoDaOrigem(opcao) {
      if (!('integracaoId' in opcao)) return 0;
      if (String(opcao.integracaoId || '') === this.consulta.integracaoId) return 0;

      return opcao.integracaoId ? 2 : 1;
    },

    limpar() {
      this.consulta.integracaoId = '';
      this.aoTrocarIntegracao();
    },

    diagnosticar() {
      if (!this.podeConsultar) return;

      this.loading = true;
      this.resultado = null;

      const params = new URLSearchParams({
        cor_id: this.cor.selecionado.valor,
        ean_base: this.base.selecionado.valor,
        filial_id: this.consulta.filialId,
      });

      if (this.cliente.selecionado) params.append('cliente_id', this.cliente.selecionado.valor);
      params.append('canal', this.consulta.canal);

      get(`/admin/tintometrico/diagnostico?${params}`)
        .then((json) => {
          this.resultado = json.data || json;
          this.consultado = this.resumoDaConsulta();
        })
        .catch((e) => { this.$swal({ title: 'Erro', text: e.message, icon: 'error' }); })
        .finally(() => { this.loading = false; });
    },

    // Congela o que foi perguntado: mexer nos campos depois não pode fazer o
    // resultado na tela parecer de outra consulta.
    resumoDaConsulta() {
      const filial = this.filiaisDaIntegracao.find((f) => f.id === this.consulta.filialId);

      return {
        integracao: this.integracao ? this.integracao.descricao : '',
        filial: filial ? filial.nome : this.consulta.filialId,
        cor: `${this.cor.selecionado.nome} (${this.cor.selecionado.cod})`,
        base: `${this.base.selecionado.nome} · ${this.base.selecionado.ean}`,
        cliente: this.cliente.selecionado ? this.cliente.selecionado.nome : '',
        canal: (CANAIS.find((c) => c.valor === this.consulta.canal) || {}).nome || '',
      };
    },

    classeSituacao(situacao) {
      return { ok: 'is-ok', falhou: 'is-falha' }[situacao] || 'is-neutro';
    },
  },
};
</script>
